import DocumentAssignmentNotice from "../components/DocumentAssignmentNotice";
import { useInHub } from "../lib/hubContext";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { Link, useSearchParams } from "react-router-dom";

import {
  excludeSourceFile,
  fetchSourceCoverageSummary,
  fetchSourceFileDetail,
  fetchSourceFileInventory,
  fetchSourceFileStatusHistory,
  fetchSourceReadModelStatus,
  refreshSourceReadModel,
  restoreSourceFile,
  type SourceCoverageDomainSummary,
  type SourceFileFormatSummary,
  type SourceFileRow,
  type SourceFileSortBy,
  type SourceFileSortDir,
  type SourceReadModelStatus,
} from "../api/sourceCoverage";
import { useSession } from "../auth/useSession";
import {
  TableEmptyRow,
  TableErrorRow,
  TableHeadCell,
  TableLoadingRow,
  TableLoadMoreFooter,
  TablePanel,
} from "../components/DataTable";
import DocumentPdfActions from "../components/DocumentPdfActions";
import FilterDropdown from "../components/FilterDropdown";
import ImportSourcesNav from "../components/ImportSourcesNav";
import SearchInput from "../components/SearchInput";
import DateInput from "../components/DateInput";
import {
  SourceBadge,
  sourceBadgeKindFromSourceKind,
} from "../components/SourceBadge";
import {
  SourceCoveragePills,
  formatCoverageCount,
} from "../components/SourceCoverage";
import { formatDateShort, toNumber } from "../lib/format";
import { canManageSources } from "../lib/role";
import { usePersonalView } from "../lib/usePersonalView";
import { useViewSearch } from "../lib/useViewSearch";
import { fileDateError } from "../lib/viewPreference";
import PersonalViewNotice from "../components/PersonalViewNotice";
import { useEscapeClosableDetails } from "../lib/useEscapeClosableDetails";
import { useEscapeKey } from "../lib/useEscapeKey";
import SupplierSourceProfilePanel from "./sourceCoverage/SupplierSourceProfilePanel";
import SectionInfo from "../components/SectionInfo";

type CoverageTab = "files" | "coverage" | "profiles";

const FILE_FORMATS: { key: string; label: string }[] = [
  { key: "pdf", label: "PDF" },
  { key: "ugl", label: "UGL" },
  { key: "datanorm", label: "DATANORM" },
  { key: "manual", label: "Manuelle Einträge" },
];

const FORMAT_FILTERS = [
  { value: "all", label: "Alle Formate" },
  { value: "pdf", label: "PDF" },
  { value: "ugl", label: "UGL" },
  { value: "datanorm", label: "DATANORM" },
  { value: "manual", label: "Manuell" },
] as const;

const SUPPLIER_FILTERS = [
  { value: "all", label: "Alle Großhändler" },
  { value: "SHT", label: "SHT" },
  { value: "GC", label: "GC" },
] as const;

const PARSER_STATUS_FILTERS = [
  { value: "all", label: "Alle Verarbeitungen" },
  { value: "not_parsed", label: "Nicht erkannt" },
  { value: "unused", label: "Nur Belegkopf" },
  { value: "used", label: "Positionen übernommen" },
] as const;

const DOC_TYPE_LABELS: Record<string, string> = {
  rechnung: "Rechnung",
  einzelauftrag: "Einzelauftrag",
  hauptabrufer: "Hauptabruf",
  angebot: "Angebot",
  gutschrift: "Gutschrift",
  retoure: "Retoure",
};

const DOC_TYPE_FILTERS = [
  { value: "all", label: "Alle Belegtypen" },
  { value: "rechnung", label: "Rechnung" },
  { value: "gutschrift", label: "Gutschrift" },
  { value: "angebot", label: "Angebot" },
  { value: "einzelauftrag", label: "Einzelauftrag" },
  { value: "hauptabrufer", label: "Hauptabruf" },
  { value: "retoure", label: "Retoure" },
] as const;

const FILE_PAGE_SIZE = 50;
const FILE_DRAWER_FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

type SourceFileSortState = {
  key: SourceFileSortBy;
  dir: SourceFileSortDir;
};

export default function SourceCoveragePage() {
  const inHub = useInHub();
  const { user } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = tabFromParam(searchParams.get("tab"));
  const selectedFileRef = normalizeSourceFileRef(searchParams.get("file"));
  const mayManageSources = canManageSources(user);

  function setSelectedFileRef(fileRef: string | null) {
    const params = new URLSearchParams(searchParams);
    if (fileRef) params.set("file", fileRef);
    else { params.delete("file"); params.set("view", "default"); }
    setSearchParams(params, { replace: !fileRef });
  }

  return (
    <main className="mx-auto w-full min-w-0 max-w-7xl px-6 py-6">
      {tab !== "files" ? <><header className="mb-5">
        {inHub ? null : <h1 className="text-2xl font-semibold tracking-tight text-slate-950">
          Dateibestand
        </h1>}
      </header>

      <ImportSourcesNav
        active={tab === "coverage" ? "coverage" : "profiles"}
      /></> : null}

      {tab !== "files" ? (
        <Link to="/coverage" className="mt-5 inline-flex text-sm font-medium text-emerald-700 hover:underline">← Zurück zu Dateien</Link>
      ) : null}
      {tab === "files" ? (
        <FilesTab
          canManageSources={mayManageSources}
          selectedFileRef={selectedFileRef}
          onSelectFileRef={setSelectedFileRef}
        />
      ) : null}
      {tab === "coverage" ? <CoverageTab /> : null}
      {tab === "profiles" ? (
        <div className="mt-5">
          <SupplierSourceProfilePanel canManageProfiles={mayManageSources} />
        </div>
      ) : null}
    </main>
  );
}

function FilesTab({
  canManageSources,
  selectedFileRef,
  onSelectFileRef,
}: {
  canManageSources: boolean;
  selectedFileRef: string | null;
  onSelectFileRef: (fileRef: string | null) => void;
}) {
  const inHub = useInHub();
  const queryClient = useQueryClient();
  const personalView = usePersonalView("files");
  const params = personalView.params;
  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (!value || value === "all") next.delete(key); else next.set(key, value);
    personalView.change(next);
  };
  const format = params.get("format") ?? "all", docType = params.get("doc_type") ?? "all";
  const supplier = params.get("supplier") ?? "all", parserStatus = params.get("parser_status") ?? "all";
  const sourceDateFrom = params.get("source_from") ?? "", sourceDateTo = params.get("source_to") ?? "";
  const setFormat = (v: string) => update("format", v), setDocType = (v: string) => update("doc_type", v);
  const setSupplier = (v: string) => update("supplier", v), setParserStatus = (v: string) => update("parser_status", v);
  const setSourceDateFrom = (v: string) => update("source_from", v), setSourceDateTo = (v: string) => update("source_to", v);
  const sort: SourceFileSortState = {
    key: (["source_date", "imported_at", "format", "parser_status", "status"].includes(params.get("sort_by") ?? "") ? params.get("sort_by") : "imported_at") as SourceFileSortBy,
    dir: params.get("sort_dir") === "asc" ? "asc" : "desc",
  };
  const { search, setSearch, searchPending } = useViewSearch(personalView);
  const debouncedSearch = params.get("q") ?? "";
  const dateError = fileDateError(params);
  const advancedFilters = useEscapeClosableDetails();
  const { setOpen: setAdvancedOpen } = advancedFilters;
  useEffect(() => {
    setAdvancedOpen(docType !== "all" || parserStatus !== "all" || Boolean(sourceDateFrom || sourceDateTo));
  }, [docType, parserStatus, sourceDateFrom, sourceDateTo, setAdvancedOpen]);
  const [selectedFileOverride, setSelectedFileOverride] =
    useState<SourceFileRow | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const readModelStatusQuery = useQuery({
    queryKey: ["source-coverage", "read-model-status"],
    queryFn: fetchSourceReadModelStatus,
    staleTime: 15_000,
  });
  const inventoryQuery = useInfiniteQuery({
    queryKey: [
      "source-files",
      {
        format,
        docType,
        supplier,
        parserStatus,
        sourceDateFrom,
        sourceDateTo,
        sort,
        q: debouncedSearch,
      },
    ],
    queryFn: ({ pageParam }) =>
      fetchSourceFileInventory({
        format,
        docType,
        supplier,
        parserStatus,
        sourceDateFrom: sourceDateFrom || undefined,
        sourceDateTo: sourceDateTo || undefined,
        sortBy: sort.key,
        sortDir: sort.dir,
        q: debouncedSearch || undefined,
        limit: FILE_PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.offset + lastPage.items.length;
      if (lastPage.items.length < lastPage.limit) return undefined;
      if (nextOffset >= lastPage.total) return undefined;
      return nextOffset;
    },
    enabled: personalView.ready && !dateError,
    staleTime: 30_000,
  });
  const firstPage = inventoryQuery.data?.pages[0];
  const summary = firstPage?.summary;
  const total = firstPage?.total ?? 0;
  const rows = useMemo(
    () => inventoryQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [inventoryQuery.data],
  );
  const selectedFileIsLoaded = rows.some(
    (row) => row.file_ref === selectedFileRef,
  );
  const selectedFileQuery = useQuery({
    queryKey: ["source-files", "detail", selectedFileRef],
    queryFn: () => fetchSourceFileDetail(selectedFileRef ?? ""),
    enabled: Boolean(selectedFileRef && !selectedFileIsLoaded),
    staleTime: 30_000,
    retry: false,
  });
  const selectedFile =
    selectedFileOverride?.file_ref === selectedFileRef
      ? selectedFileOverride
      : (rows.find((row) => row.file_ref === selectedFileRef) ??
        selectedFileQuery.data ??
        null);

  const statusMutation = useMutation({
    mutationFn: ({
      action,
      fileRef,
      reason,
    }: {
      action: "exclude" | "restore";
      fileRef: string;
      reason: string | null;
    }) =>
      action === "exclude"
        ? excludeSourceFile(fileRef, reason)
        : restoreSourceFile(fileRef, reason),
    onSuccess: (updatedFile) => {
      setSelectedFileOverride(updatedFile);
      void queryClient.invalidateQueries({ queryKey: ["source-files"] });
      void queryClient.invalidateQueries({
        queryKey: ["source-files", updatedFile.file_ref, "history"],
      });
      void queryClient.invalidateQueries({ queryKey: ["source-coverage"] });
    },
  });
  const refreshMutation = useMutation({
    mutationFn: refreshSourceReadModel,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["source-coverage"] });
      void queryClient.invalidateQueries({ queryKey: ["source-files"] });
    },
  });
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = inventoryQuery;
  const loadMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);
  const toggleSort = (key: SourceFileSortBy) => {
    const next = new URLSearchParams(params);
    next.set("sort_by", key);
    next.set("sort_dir", sort.key === key ? sort.dir === "desc" ? "asc" : "desc" : key === "parser_status" ? "asc" : "desc");
    personalView.change(next);
  };

  useEffect(() => {
    if (!hasNextPage) return;
    const target = loadMoreRef.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      },
      { rootMargin: "280px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasNextPage, loadMore, rows.length]);

  const selectFile = useCallback(
    (row: SourceFileRow) => {
      statusMutation.reset();
      setSelectedFileOverride(row);
      onSelectFileRef(row.file_ref);
    },
    [onSelectFileRef, statusMutation],
  );

  const changeFileStatus = useCallback(
    (action: "exclude" | "restore", reason: string | null) => {
      if (!selectedFile || statusMutation.isPending) return;
      statusMutation.mutate({
        action,
        fileRef: selectedFile.file_ref,
        reason,
      });
    },
    [selectedFile, statusMutation],
  );

  const showInitialLoading = !personalView.ready || searchPending || Boolean(dateError) || (inventoryQuery.isFetching && !inventoryQuery.isFetchingNextPage);
  const advancedFilterCount = [
    docType !== "all",
    parserStatus !== "all",
    Boolean(sourceDateFrom),
    Boolean(sourceDateTo),
  ].filter(Boolean).length;

  return (
    <div>
      <header className="mb-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        {inHub ? <span /> : <h1 className="text-2xl font-semibold tracking-tight text-slate-950">Dateibestand</h1>}
        <div className="flex flex-wrap items-center gap-2">
          <PersonalViewNotice withReset view={personalView} defaultLabel="Alle Dateien, zuletzt importierte zuerst."
            className="flex flex-wrap items-center gap-2 text-xs text-slate-600" />
          <ImportSourcesNav active="inventory" />
        </div>
      </header>
      {dateError && <p role="alert" className="mb-3 text-sm text-amber-800">{dateError}</p>}
      <FormatSummaryStrip formats={showInitialLoading ? [] : summary?.formats ?? []} />

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <SearchInput disabled={!personalView.ready}
          value={search}
          onChange={setSearch}
          placeholder="Dateiname, Belegtyp…"
          ariaLabel="Datei suchen"
        />
        <FilterDropdown
          label="Format"
          ariaLabel="Nach Format filtern"
          options={FORMAT_FILTERS}
          value={format}
          defaultValue="all"
          onChange={setFormat}
        />
        <FilterDropdown
          label="Großhändler"
          ariaLabel="Nach Großhändler filtern"
          options={SUPPLIER_FILTERS.some(option => option.value === supplier) ? SUPPLIER_FILTERS : [...SUPPLIER_FILTERS, { value: supplier, label: supplier }]}
          value={supplier}
          defaultValue="all"
          onChange={setSupplier}
        />
      </div>

      <details
        open={advancedFilters.open}
        className="mt-3 rounded-lg border border-slate-200 bg-white px-4 py-3"
      >
        <summary
          ref={advancedFilters.summaryRef}
          onClick={advancedFilters.onSummaryClick}
          className="cursor-pointer rounded-sm text-sm font-medium text-slate-700 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-1"
        >
          Weitere Filter
          {advancedFilterCount > 0
            ? ` · ${advancedFilterCount.toLocaleString("de-AT")} aktiv`
            : ""}
        </summary>
        <div className="mt-3 flex flex-wrap items-end gap-3 border-t border-slate-100 pt-3">
          <FilterDropdown
            label="Belegtyp"
            ariaLabel="Nach Belegtyp filtern"
            options={DOC_TYPE_FILTERS}
            value={docType}
            defaultValue="all"
            onChange={setDocType}
          />
          <FilterDropdown
            label="PDF-Verarbeitung"
            ariaLabel="Nach PDF-Verarbeitung filtern"
            options={PARSER_STATUS_FILTERS}
            value={parserStatus}
            defaultValue="all"
            onChange={setParserStatus}
          />
          <label className="flex min-w-[9rem] flex-col gap-1 text-xs text-slate-600">
            Belegdatum von
            <DateInput
              value={sourceDateFrom}
              onChange={setSourceDateFrom}
              ariaLabel="Belegdatum von wählen"
              placeholder="Von"
            />
          </label>
          <label className="flex min-w-[9rem] flex-col gap-1 text-xs text-slate-600">
            Belegdatum bis
            <DateInput
              value={sourceDateTo}
              onChange={setSourceDateTo}
              ariaLabel="Belegdatum bis wählen"
              placeholder="Bis"
            />
          </label>
        </div>
      </details>

      <ReadModelRefreshStatus
        status={readModelStatusQuery.data}
        isLoading={readModelStatusQuery.isLoading}
        isError={readModelStatusQuery.isError}
        isRefreshing={refreshMutation.isPending}
        error={
          refreshMutation.error instanceof Error
            ? refreshMutation.error.message
            : null
        }
        canRefresh={canManageSources}
        onRefresh={() => refreshMutation.mutate()}
      />

      {selectedFileRef && selectedFileQuery.isError && !selectedFile ? (
        <div
          role="alert"
          className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800"
        >
          <span>
            Die ausgewählte Quelle ist nicht verfügbar oder darf nicht geöffnet
            werden.
          </span>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="inline-flex h-9 items-center rounded-md border border-rose-200 bg-white px-3 text-sm font-medium text-rose-800 hover:bg-rose-100"
              onClick={() => void selectedFileQuery.refetch()}
            >
              Erneut laden
            </button>
            <button
              type="button"
              className="inline-flex h-9 items-center rounded-md px-3 text-sm font-medium text-slate-700 hover:bg-white"
              onClick={() => onSelectFileRef(null)}
            >
              Schließen
            </button>
          </div>
        </div>
      ) : null}

      <div className="mt-4">
        <TablePanel
          title="Dateien"
          subtitle="Öffne eine Datei, um das Original und die Importdetails anzusehen."
          footer={
            showInitialLoading || inventoryQuery.isError ? null : (
              <TableLoadMoreFooter
                loadMoreRef={loadMoreRef}
                loadedCount={rows.length}
                totalCount={total}
                itemLabel="Dateien"
                moreItemLabel="Dateien"
                hasMore={Boolean(hasNextPage)}
                isLoading={isFetchingNextPage}
                buttonLabel="Weitere Dateien laden"
                loadingLabel="Lade weitere Dateien …"
                onLoadMore={loadMore}
              />
            )
          }
        >
          <table className="w-full min-w-[700px] table-fixed divide-y divide-slate-200 text-sm">
            <colgroup>
              <col className="w-[32%]" />
              <col className="w-[15%]" />
              <col className="w-[14%]" />
              <col className="w-[18%]" />
              <col className="w-[21%]" />
            </colgroup>
            <thead>
              <tr>
                <TableHeadCell>Datei</TableHeadCell>
                <TableHeadCell>Belegtyp</TableHeadCell>
                <TableHeadCell>Großhändler</TableHeadCell>
                <SortableSourceHeadCell
                  label="Belegdatum"
                  sortKey="source_date"
                  activeKey={sort.key}
                  dir={sort.dir}
                  align="right"
                  onClick={() => toggleSort("source_date")}
                />
                <SortableSourceHeadCell
                  label="Status"
                  sortKey="status"
                  activeKey={sort.key}
                  dir={sort.dir}
                  onClick={() => toggleSort("status")}
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {dateError ? <TableEmptyRow colSpan={5}>Bitte korrigiere den Belegzeitraum.</TableEmptyRow> : showInitialLoading ? (
                <TableLoadingRow colSpan={5} label="Lade Dateien …" />
              ) : inventoryQuery.isError ? (
                <TableErrorRow colSpan={5}>
                  Dateiliste konnte nicht geladen werden.
                </TableErrorRow>
              ) : rows.length === 0 ? (
                <TableEmptyRow colSpan={5}>
                  Keine Dateien gefunden.
                </TableEmptyRow>
              ) : (
                rows.map((row) => (
                  <SourceFileTableRow
                    key={row.file_ref}
                    row={row}
                    onSelect={selectFile}
                  />
                ))
              )}
            </tbody>
          </table>
        </TablePanel>
      </div>

      <FileDetailDrawer
        file={selectedFile}
        canManageSources={canManageSources}
        onClose={() => {
          statusMutation.reset();
          setSelectedFileOverride(null);
          onSelectFileRef(null);
        }}
        onStatusChange={changeFileStatus}
        statusError={
          statusMutation.error instanceof Error
            ? statusMutation.error.message
            : null
        }
        statusPending={statusMutation.isPending}
      />
    </div>
  );
}

function FormatSummaryStrip({ formats }: { formats: SourceFileFormatSummary[] }) {
  return (
    <section aria-label="Datenbasis je Format" className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-600">
      {formats.filter((entry) => coverageNumber(entry.file_count) > 0).map((entry) => (
        <span key={entry.format} className="inline-flex items-center gap-2">
          <SourceBadge kind={sourceBadgeKindFromSourceKind(entry.format)} label={formatLabel(entry.format)} size="sm" />
          <span className="tabular-nums">{formatCoverageCount(entry.file_count)} Dateien</span>
        </span>
      ))}
      <Link to="/coverage?tab=coverage" className="ml-auto inline-flex h-9 items-center font-medium text-emerald-700 hover:underline">Datenabdeckung ansehen →</Link>
    </section>
  );
}

function SortableSourceHeadCell({
  label,
  sortKey,
  activeKey,
  dir,
  align = "left",
  onClick,
}: {
  label: string;
  sortKey: SourceFileSortBy;
  activeKey: SourceFileSortBy;
  dir: SourceFileSortDir;
  align?: "left" | "right";
  onClick: () => void;
}) {
  const active = activeKey === sortKey;
  const indicator = active ? (dir === "asc" ? "▲" : "▼") : "↕";
  return (
    <th
      scope="col"
      aria-sort={active ? sortAria(dir) : "none"}
      className={`px-3 py-2 font-medium ${align === "right" ? "text-right" : "text-left"}`}
    >
      <button
        type="button"
        onClick={onClick}
        className={[
          "inline-flex items-center gap-1 whitespace-nowrap",
          align === "right" ? "justify-end" : "justify-start",
          active ? "text-emerald-700" : "text-slate-500 hover:text-slate-700",
        ].join(" ")}
      >
        {label}
        <span className="text-[10px] opacity-70">{indicator}</span>
      </button>
    </th>
  );
}

function sortAria(dir: SourceFileSortDir): "ascending" | "descending" {
  return dir === "asc" ? "ascending" : "descending";
}

function ReadModelRefreshStatus({
  status,
  isLoading,
  isError,
  isRefreshing,
  error,
  canRefresh,
  onRefresh,
}: {
  status: SourceReadModelStatus | undefined;
  isLoading: boolean;
  isError: boolean;
  isRefreshing: boolean;
  error: string | null;
  canRefresh: boolean;
  onRefresh: () => void;
}) {
  const pendingCount = toNumber(status?.pending_event_count) ?? 0;
  const excludedCount = toNumber(status?.excluded_file_count) ?? 0;
  const excludedWholesaleCount =
    toNumber(status?.excluded_wholesale_batch_count) ?? 0;
  const requiresFullRefresh = status?.pending_refresh_scope === "full";
  const pending = Boolean(status?.pending);
  if (isLoading || (!pending && !isError && !error)) return null;
  const label = isError
    ? "Auswertungsstand nicht verfügbar"
    : `${formatCoverageCount(pendingCount)} Quellenänderungen offen`;
  const pendingHint = requiresFullRefresh
    ? "Die Aktualisierung umfasst auch Katalogdaten und kann länger dauern."
    : "Aktualisierung kann einige Minuten dauern.";
  const refreshHint = pending
    ? `Ausgenommen: ${formatCoverageCount(excludedCount)} Dateien und ${formatCoverageCount(excludedWholesaleCount)} Importstände. ${pendingHint}`
    : "Der aktuelle Auswertungsstand konnte nicht geladen werden.";
  const toneClass = pending
    ? "border-amber-200 bg-amber-50/70"
    : "border-rose-200 bg-rose-50/60";

  return (
    <section
      aria-label="Auswertungsstand"
      className={`mt-3 flex min-w-0 flex-wrap items-center gap-3 rounded-lg border px-4 py-3 ${toneClass}`}
    >
      <div className="min-w-0 flex-1">
        <h2 className="text-sm font-semibold text-slate-900">
          Auswertungsstand
        </h2>
        <div
          className={[
            "mt-1 text-sm font-medium",
            pending ? "text-amber-900" : isError ? "text-rose-800" : "text-slate-700",
          ].join(" ")}
        >
          {label}
        </div>
        <div className="mt-0.5 text-xs text-slate-500">
          {refreshHint}
        </div>
        {error ? (
          <div className="mt-1 text-xs font-medium text-rose-700">{error}</div>
        ) : null}
      </div>
      {pending && canRefresh ? (
        <button
          type="button"
          disabled={isLoading || isError || isRefreshing}
          onClick={onRefresh}
          className="inline-flex h-9 shrink-0 items-center rounded-md bg-slate-900 px-4 text-sm font-medium text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500 disabled:shadow-none"
        >
          {isRefreshing ? "Aktualisiere…" : "Auswertungen aktualisieren"}
        </button>
      ) : pending && !canRefresh ? (
        <p className="w-full text-xs text-slate-500 lg:w-auto lg:max-w-48">
          Für die Aktualisierung fehlt Ihnen der Rollenbereich Administrator.
        </p>
      ) : null}
    </section>
  );
}

function SourceFileTableRow({
  row,
  onSelect,
}: {
  row: SourceFileRow;
  onSelect: (row: SourceFileRow) => void;
}) {
  const isExcluded = row.status === "excluded";
  return (
    <tr
      className={[
        isExcluded ? "bg-slate-50" : "hover:bg-slate-50",
      ].join(" ")}
    >
      <td className="px-3 py-2">
        <button
          type="button"
          onClick={(event) => {
            // Safari does not focus buttons on pointer activation by default.
            // Capture this trigger before the drawer takes keyboard focus.
            event.currentTarget.focus();
            onSelect(row);
          }}
          className={[
            "block max-w-full truncate rounded-sm text-left font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1",
            isExcluded
              ? "text-slate-500"
              : "text-slate-900 hover:text-emerald-700",
          ].join(" ")}
          title={row.filename}
        >
          {row.filename || "Datei"}
        </button>
        <SourceBadge
          kind={sourceBadgeKindFromSourceKind(row.format)}
          label={formatLabel(row.format)}
          size="sm"
          className="mt-1 uppercase"
        />
      </td>
      <td className="px-3 py-2 text-slate-700">{docTypeLabel(row.doc_type)}</td>
      <td className="px-3 py-2 text-slate-700">{row.supplier || "–"}</td>
      <td className="px-3 py-2 text-right tabular-nums text-slate-600">
        {formatSourceDateRange(row.source_date_from, row.source_date_to)}
      </td>
      <td className="px-3 py-2">
        {(!row.status || ["imported", "completed"].includes(row.status)) && row.format === "pdf" && row.parser_status ? (
          <ParserStatusBadge status={row.parser_status} format={row.format} />
        ) : <FileStatusBadge status={row.status} />}
      </td>
    </tr>
  );
}

function FileDetailDrawer({
  file,
  canManageSources,
  onClose,
  onStatusChange,
  statusError,
  statusPending,
}: {
  file: SourceFileRow | null;
  canManageSources: boolean;
  onClose: () => void;
  onStatusChange: (action: "exclude" | "restore", reason: string | null) => void;
  statusError: string | null;
  statusPending: boolean;
}) {
  const [reason, setReason] = useState("");
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const open = Boolean(file);
  const fileRef = file?.file_ref ?? "";
  const historyQuery = useQuery({
    queryKey: ["source-files", fileRef, "history"],
    queryFn: () => fetchSourceFileStatusHistory(fileRef),
    enabled: Boolean(fileRef),
    staleTime: 30_000,
  });
  useEscapeKey(onClose, open);
  useEffect(() => {
    setReason("");
  }, [file?.file_ref, file?.status]);
  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const frame = window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });
    return () => {
      window.cancelAnimationFrame(frame);
      document.body.style.overflow = previousOverflow;
      restoreFocusRef.current?.focus();
      restoreFocusRef.current = null;
    };
  }, [open]);
  if (!open || !file) return null;
  const isExcluded = file.status === "excluded";
  const isBatchScoped = isWholesaleBatchScoped(file);
  const subjectLabel = isBatchScoped ? "Importstand" : "Datei";
  const statusAction = isExcluded ? "restore" : "exclude";
  const statusDescription = isBatchScoped
    ? "Die Änderung gilt für den gesamten Importstand und wird protokolliert. Katalog- und Auswertungsdaten ändern sich nach der nächsten Aktualisierung."
    : "Die Änderung gilt für diese Datei und wird protokolliert. Auswertungen ändern sich nach der nächsten Aktualisierung.";
  const statusButtonLabel = statusPending
    ? "Speichere…"
    : isExcluded
      ? `${subjectLabel} wieder aktivieren`
      : `${subjectLabel} von Auswertungen ausschließen`;
  const reasonRequired = !isExcluded;
  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        className="absolute inset-0 cursor-default bg-slate-900/20"
        aria-label="Datei-Detail schließen"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="file-detail-title"
        onKeyDown={trapFileDrawerFocus}
        className="absolute inset-y-0 right-0 flex w-[min(34rem,100vw)] flex-col border-l border-slate-200 bg-white shadow-2xl"
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {subjectLabel}
            </div>
            <h2
              id="file-detail-title"
              className="mt-1 break-words text-lg font-semibold text-slate-900"
            >
              {file.filename || "Datei"}
            </h2>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="h-8 rounded-md px-2 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          >
            Schließen
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
          {isBatchScoped ? (
            <p className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm leading-5 text-amber-900">
              Dieser Importstand umfasst{" "}
              <span className="font-semibold tabular-nums">
                {formatCoverageCount(file.batch_file_count)} Dateien
              </span>
              . Statusänderungen wirken auf den gesamten Bestand, nicht nur auf
              die hier benannte Quelldatei.
            </p>
          ) : null}
          <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            <DetailField label="Format" value={formatLabel(file.format)} />
            <DetailField label="Belegtyp" value={docTypeLabel(file.doc_type)} />
            <DetailField label="Großhändler" value={file.supplier || "–"} />
            <DetailField
              label="Belegdatum / Zeitraum"
              value={formatSourceDateRange(
                file.source_date_from,
                file.source_date_to,
              )}
            />
            <DetailField
              label="Importiert"
              value={formatImportDate(file.imported_at)}
            />
            <DetailField label="Größe" value={formatBytes(file.size_bytes)} />
            <DetailField
              label="Status"
              value={<FileStatusBadge status={file.status} />}
            />
            <DetailField
              label="PDF-Verarbeitung"
              value={
                <ParserStatusBadge
                  status={file.parser_status}
                  format={file.format}
                />
              }
            />
            {file.format === "pdf" ? (
              <DetailField
                label="Belege / Zeilen"
                value={`${formatCoverageCount(file.document_count)} / ${formatCoverageCount(file.line_count)}`}
              />
            ) : null}
            {file.parser_status_reason ? (
              <DetailField
                label="Hinweis zur PDF-Verarbeitung"
                value={file.parser_status_reason}
              />
            ) : null}
            {file.status_updated_at ? (
              <DetailField
                label="Statusänderung"
                value={formatImportDate(file.status_updated_at)}
              />
            ) : null}
            {file.status_reason ? (
              <DetailField label="Grund" value={file.status_reason} />
            ) : null}
            {isBatchScoped ? (
              <DetailField
                label="Importstand"
                value={
                  file.batch_file_count
                    ? `${formatCoverageCount(file.batch_file_count)} Dateien betroffen`
                    : "Wholesale-Batch"
                }
              />
            ) : null}
          </dl>
          {file.pdf_doc_id ? (
            <DocumentPdfActions
              docId={file.pdf_doc_id}
              docNumber={file.filename}
              openActionLabel="Ganzes PDF öffnen"
              className="mt-4"
            />
          ) : file.format === "pdf" ? (
            <p className="mt-4 text-xs text-slate-400">
              PDF-Vorschau noch nicht verfügbar.
            </p>
          ) : null}
          {file.pdf_doc_id ? <DocumentAssignmentNotice docId={file.pdf_doc_id} /> : null}
          {canManageSources ? (
            <details className="mt-5 border-t border-slate-200 pt-4">
              <summary className="cursor-pointer text-sm font-medium text-slate-700">Weitere Aktionen</summary>
              <h3 className="mt-3 text-sm font-semibold text-slate-900">
                Quellenstatus verwalten
              </h3>
              <p className="mt-1 text-xs text-slate-500">
                {statusDescription}
              </p>
              <label className="mt-3 block text-xs font-medium text-slate-600">
                Grund{reasonRequired ? " *" : " optional"}
                <textarea
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  maxLength={1000}
                  rows={3}
                  className="mt-1 block w-full resize-none rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </label>
              {statusError ? (
                <p className="mt-2 text-xs font-medium text-rose-700">
                  {statusError}
                </p>
              ) : null}
              <button
                type="button"
                onClick={() =>
                  onStatusChange(statusAction, reason.trim() || null)
                }
                disabled={
                  statusPending || (reasonRequired && reason.trim().length === 0)
                }
                className={[
                  "mt-3 inline-flex h-9 items-center rounded-md px-4 text-sm font-medium text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-60",
                  isExcluded
                    ? "bg-emerald-700 hover:bg-emerald-800"
                    : "bg-rose-700 hover:bg-rose-800",
                ].join(" ")}
              >
                {statusButtonLabel}
              </button>
            </details>
          ) : null}
          <section className="mt-5 border-t border-slate-200 pt-4">
            <h3 className="text-sm font-semibold text-slate-900">
              Statushistorie
            </h3>
            {historyQuery.isLoading ? (
              <p className="mt-2 text-xs text-slate-500">Lade Historie …</p>
            ) : historyQuery.isError ? (
              <p className="mt-2 text-xs font-medium text-rose-700">
                Statushistorie konnte nicht geladen werden.
              </p>
            ) : historyQuery.data?.events.length ? (
              <ol className="mt-3 space-y-3">
                {historyQuery.data.events.map((event, index) => (
                  <li
                    key={`${event.created_at ?? "event"}-${index}`}
                    className="rounded-md border border-slate-100 p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <span
                        className={[
                          "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
                          event.action === "exclude"
                            ? "bg-rose-100 text-rose-700"
                            : "bg-emerald-100 text-emerald-700",
                        ].join(" ")}
                      >
                        {sourceStatusActionLabel(event.action)}
                      </span>
                      <span className="shrink-0 text-xs tabular-nums text-slate-500">
                        {formatImportDate(event.created_at)}
                      </span>
                    </div>
                    {event.reason ? (
                      <p className="mt-2 text-sm text-slate-700">
                        {event.reason}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="mt-2 text-xs text-slate-500">
                Noch keine Statusänderung.
              </p>
            )}
          </section>
        </div>
      </aside>
    </div>
  );
}

function trapFileDrawerFocus(event: KeyboardEvent<HTMLElement>) {
  if (event.key !== "Tab") return;
  const focusable = Array.from(
    event.currentTarget.querySelectorAll<HTMLElement>(
      FILE_DRAWER_FOCUSABLE_SELECTOR,
    ),
  ).filter((element) => element.getClientRects().length > 0);
  const first = focusable[0];
  const last = focusable.at(-1);
  if (!first || !last) {
    event.preventDefault();
    return;
  }
  const active = document.activeElement;
  if (
    event.shiftKey &&
    (active === first || !event.currentTarget.contains(active))
  ) {
    event.preventDefault();
    last.focus();
    return;
  }
  if (
    !event.shiftKey &&
    (active === last || !event.currentTarget.contains(active))
  ) {
    event.preventDefault();
    first.focus();
  }
}

function sourceStatusActionLabel(action: string): string {
  if (action === "exclude") return "Ausgenommen";
  if (action === "restore") return "Wieder aktiviert";
  return action || "Statusänderung";
}

function isWholesaleBatchScoped(file: SourceFileRow): boolean {
  return file.status_scope === "wholesale_batch";
}

function DetailField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0 rounded-md border border-slate-100 p-3">
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="mt-1 break-words font-medium text-slate-900">{value}</dd>
    </div>
  );
}

function formatBytes(value: number | string | null | undefined): string {
  const bytes = toNumber(value);
  if (!bytes || bytes <= 0) return "–";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function FileStatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-slate-400">–</span>;
  const map: Record<string, { label: string; className: string }> = {
    imported: {
      label: "Importiert",
      className: "bg-emerald-100 text-emerald-700",
    },
    completed: {
      label: "Importiert",
      className: "bg-emerald-100 text-emerald-700",
    },
    review: { label: "Prüfung", className: "bg-amber-100 text-amber-700" },
    review_needed: { label: "Prüfung", className: "bg-amber-100 text-amber-700" },
    staged: { label: "Bereitgestellt", className: "bg-slate-100 text-slate-600" },
    excluded: { label: "Ausgenommen", className: "bg-slate-200 text-slate-700" },
  };
  const badge = map[status] ?? {
    label: status,
    className: "bg-slate-100 text-slate-600",
  };
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}
    >
      {badge.label}
    </span>
  );
}

function ParserStatusBadge({
  status,
  format,
}: {
  status: string | null | undefined;
  format: string | null | undefined;
}) {
  if (format !== "pdf") return <span className="text-slate-400">–</span>;
  const map: Record<string, { label: string; className: string }> = {
    used: {
      label: "Positionen übernommen",
      className: "bg-emerald-100 text-emerald-700",
    },
    unused: {
      label: "Nur Belegkopf",
      className: "bg-amber-100 text-amber-800",
    },
    not_parsed: {
      label: "Nicht erkannt",
      className: "bg-amber-100 text-amber-700",
    },
  };
  const badge = status ? map[status] : null;
  if (!badge) return <span className="text-slate-400">–</span>;
  return (
    <span
      className={`inline-flex min-w-[6.75rem] items-center justify-center whitespace-nowrap rounded-full px-2 py-0.5 text-center text-xs font-medium ${badge.className}`}
    >
      {badge.label}
    </span>
  );
}

function CoverageTab() {
  const summaryQuery = useQuery({
    queryKey: ["source-coverage", "summary"],
    queryFn: fetchSourceCoverageSummary,
  });
  const [onlyNeedsClarification, setOnlyNeedsClarification] = useState(false);
  const domains = summaryQuery.data?.domains ?? [];
  const visibleDomains = onlyNeedsClarification
    ? domains.filter(
        (domain) =>
          positive(domain.review_needed) ||
          positive(domain.source_gap) ||
          positive(domain.unknown),
      )
    : domains;

  return (
    <div>
      <section
        aria-label="Datenabdeckung je Bereich"
        className="rounded-lg border border-slate-200 bg-white shadow-sm"
      >
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 px-4 py-4">
          <div className="max-w-3xl">
            <div className="flex items-center gap-1.5">
              <h2 className="text-base font-semibold text-slate-900">
              Datenabdeckung
            </h2>
              <SectionInfo label="Datenabdeckung">
                Welche Daten sind vollständig belegt, wo ist eine fachliche Prüfung nötig und wo fehlen Nachweise? Die Zahlen gelten jeweils für den angezeigten Bereich.
              </SectionInfo>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              aria-pressed={onlyNeedsClarification}
              onClick={() =>
                setOnlyNeedsClarification((current) => !current)
              }
              className={[
                "inline-flex h-9 items-center rounded-md border px-3 text-sm font-medium transition-colors",
                onlyNeedsClarification
                  ? "border-amber-300 bg-amber-50 text-amber-900"
                  : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
              ].join(" ")}
            >
              Nur mit Klärungsbedarf
            </button>
          </div>
        </div>

        {summaryQuery.isLoading ? (
          <div className="px-4 py-10 text-center text-sm text-slate-500">
            Datenabdeckung wird geladen …
          </div>
        ) : summaryQuery.isError ? (
          <div className="px-4 py-10 text-center text-sm text-rose-700">
            Datenabdeckung konnte nicht geladen werden.
          </div>
        ) : visibleDomains.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-slate-500">
            Keine Bereiche mit Klärungsbedarf.
          </div>
        ) : (
          <div className="grid gap-4 p-4 lg:grid-cols-3">
            {visibleDomains.map((domain) => (
              <CoverageDomainCard key={domain.domain} domain={domain} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function CoverageDomainCard({
  domain,
}: {
  domain: SourceCoverageDomainSummary;
}) {
  const details = useEscapeClosableDetails();
  const total = coverageNumber(domain.total);
  const complete = coverageNumber(domain.complete);
  const reviewNeeded = coverageNumber(domain.review_needed);
  const sourceGap = coverageNumber(domain.source_gap);
  const unsupported = coverageNumber(domain.unsupported);
  const unknown = coverageNumber(domain.unknown);
  return (
    <article className="flex min-w-0 flex-col rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="min-w-0 font-semibold text-slate-900">
          {domain.label}
        </h3>
        <Link
          to={domain.href}
          className="inline-flex h-8 shrink-0 items-center rounded-md border border-slate-200 px-2.5 text-xs font-medium text-slate-700 hover:border-slate-300 hover:bg-slate-50"
        >
          {domain.label} öffnen →
        </Link>
      </div>
      <div className="mt-3">
        <p className="text-sm text-slate-600">
          <span className="font-medium tabular-nums text-slate-900">
            {formatCoverageCount(complete)} von {formatCoverageCount(total)}
          </span>{" "}
          vollständig
        </p>
        <p className="mt-1 text-sm text-slate-600"><span className="font-semibold tabular-nums text-amber-800">{formatCoverageCount(reviewNeeded)}</span> fachlich zu prüfen</p>
        <p className="mt-1 text-sm text-slate-600"><span className="font-semibold tabular-nums">{formatCoverageCount(sourceGap)}</span> mit fehlenden Nachweisen</p>
        {unknown > 0 ? <p className="mt-1 text-sm text-slate-500">{formatCoverageCount(unknown)} noch nicht bewertet</p> : null}
      </div>

      <CoverageDistribution
        total={total}
        complete={complete}
        reviewNeeded={reviewNeeded}
        sourceGap={sourceGap}
      />

      <details
        open={details.open}
        className="mt-4 border-t border-slate-100 pt-3"
      >
        <summary
          ref={details.summaryRef}
          onClick={details.onSummaryClick}
          className="cursor-pointer rounded-sm text-xs font-medium text-slate-600 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-1"
        >
          Statusdetails und Quellen
        </summary>
        <dl className="mt-3 grid gap-2 sm:grid-cols-3">
          <CoverageStatusValue
            label="Vollständig"
            value={complete}
            className="text-emerald-800"
          />
          <CoverageStatusValue
            label="Fachlich zu prüfen"
            value={reviewNeeded}
            className="text-amber-800"
          />
          <CoverageStatusValue
            label="Nachweis nicht vollständig"
            value={sourceGap}
            className="text-slate-700"
          />
        </dl>
        {unknown > 0 ? (
          <p className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600">
            Noch nicht klassifiziert:{" "}
            <span className="font-medium tabular-nums text-slate-800">
              {formatCoverageCount(unknown)}
            </span>
          </p>
        ) : null}
        {unsupported > 0 ? (
          <p className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600">
            Durch vorhandene Quelldaten begrenzt: Für{" "}
            <span className="font-medium tabular-nums text-slate-800">
              {formatCoverageCount(unsupported)}
            </span>{" "}
            Einheiten liefert die konfigurierte Quelle keinen Nachweis. Dieser
            Hinweis kann zusätzlich zu einem anderen Status auftreten.
          </p>
        ) : null}
        <div className="mt-4">
          <div className="text-xs font-medium text-slate-500">
            Nachweisquellen
          </div>
          <SourceCoveragePills
            className="mt-2"
            ariaLabel={`${domain.label}: Nachweisquellen, Mehrfachnennungen möglich`}
            sources={[
              {
                key: "pdf",
                label: `PDF ${formatCoverageCount(domain.with_pdf_evidence)}`,
                active: positive(domain.with_pdf_evidence),
                tone: "pdf",
              },
              {
                key: "ugl",
                label: `UGL ${formatCoverageCount(domain.with_ugl_evidence)}`,
                active: positive(domain.with_ugl_evidence),
                tone: "ugl",
              },
              {
                key: "datanorm",
                label: `DATANORM ${formatCoverageCount(
                  domain.with_datanorm_evidence,
                )}`,
                active: positive(domain.with_datanorm_evidence),
                tone: "datanorm",
              },
              {
                key: "manual",
                label: `Manuell ${formatCoverageCount(
                  domain.with_manual_evidence,
                )}`,
                active: positive(domain.with_manual_evidence),
                tone: "manual",
              },
            ]}
          />
          <p className="mt-2 text-xs text-slate-500">
            Ein Objekt kann durch mehrere Quellen belegt sein.
          </p>
        </div>
      </details>
    </article>
  );
}

function CoverageDistribution({
  total,
  complete,
  reviewNeeded,
  sourceGap,
}: {
  total: number;
  complete: number;
  reviewNeeded: number;
  sourceGap: number;
}) {
  const segments = [
    {
      label: "Vollständig",
      value: complete,
      barClass: "bg-emerald-500",
    },
    {
      label: "Zu klären",
      value: reviewNeeded,
      barClass: "bg-amber-400",
    },
    {
      label: "Nachweis nicht vollständig",
      value: sourceGap,
      barClass: "bg-slate-300",
    },
  ];
  return (
    <div className="mt-4">
      <div
        role="img"
        aria-label={segments
          .map(
            (segment) =>
              `${segment.label}: ${formatCoverageCount(segment.value)}`,
          )
          .join(", ")}
        className="flex h-2 overflow-hidden rounded-full bg-slate-100"
      >
        {segments.map((segment) =>
          segment.value > 0 && total > 0 ? (
            <span
              key={segment.label}
              className={segment.barClass}
              style={{ width: `${(segment.value / total) * 100}%` }}
            />
          ) : null,
        )}
      </div>
    </div>
  );
}

function CoverageStatusValue({
  label,
  value,
  className,
}: {
  label: string;
  value: number;
  className: string;
}) {
  return (
    <div className="flex flex-col rounded-md bg-slate-50 px-3 py-2">
      <dt className="min-h-12 text-xs leading-4 text-slate-500">{label}</dt>
      <dd className={`mt-1 font-semibold tabular-nums ${className}`}>
        {formatCoverageCount(value)}
      </dd>
    </div>
  );
}

function tabFromParam(value: string | null): CoverageTab {
  if (value === "review" || value === "coverage") return "coverage";
  if (value === "profiles") return "profiles";
  return "files";
}

function normalizeSourceFileRef(value: string | null): string | null {
  const normalized = value?.trim() ?? "";
  return /^src:[a-f0-9]{12}$/.test(normalized) ? normalized : null;
}

function formatLabel(format: string): string {
  const entry = FILE_FORMATS.find((item) => item.key === format);
  return entry?.label ?? format.toUpperCase();
}

function docTypeLabel(docType: string | null): string {
  if (!docType) return "–";
  return DOC_TYPE_LABELS[docType] ?? docType;
}

function formatImportDate(value: string | null | undefined): string {
  if (!value) return "–";
  return formatDateShort(value);
}

function formatSourceDateRange(
  from: string | null | undefined,
  to: string | null | undefined,
): string {
  if (from && to && from !== to) {
    return `${formatDateShort(from)} - ${formatDateShort(to)}`;
  }
  return formatImportDate(from ?? to);
}

function coverageNumber(
  value: string | number | null | undefined,
): number {
  return Math.max(0, toNumber(value) ?? 0);
}

function positive(value: string | number | null | undefined): boolean {
  return (toNumber(value) ?? 0) > 0;
}
